/**
 * Experiences Page
 * Main page for browsing all experiences
 */

import React from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { ThemeProvider } from '@/design-system/themes';
import { Sidebar } from '@/components/navbar/web/Sidebar';
import { MobileNavbar } from '@/components/navbar/mobile/MobileNavbar';
import { Footer } from '@/components/common/Footer';
import SeoHead, {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_META_TITLE,
} from '../../components/common/SeoHead';
import { backgroundColors, spacing } from '@/design-system/utils';
import { ExperiencesLoadingPlaceholder } from '@/components/experiences/ExperiencesLoadingPlaceholder';

// Loading component that shows immediately while container loads
const ExperiencesLoadingWrapper = () => {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '1343px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Show a minimal header/filter structure immediately */}
      <div
        style={{
          width: '100%',
          maxWidth: '853px',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
          alignItems: 'center',
          paddingTop: spacing[6],
          paddingBottom: spacing[4],
        }}
      >
        {/* Title placeholder */}
        <div
          style={{
            height: '32px',
            width: '300px',
            backgroundColor: '#E5E7EB',
            borderRadius: '4px',
            marginBottom: spacing[2],
          }}
        />
        {/* Filter chips placeholder */}
        <div
          style={{
            display: 'flex',
            gap: spacing[2],
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              style={{
                height: '36px',
                width: '120px',
                backgroundColor: '#E5E7EB',
                borderRadius: '20px',
              }}
            />
          ))}
        </div>
      </div>
      {/* Loading placeholder */}
      <ExperiencesLoadingPlaceholder pattern='initial' cardsPerSection={6} />
    </div>
  );
};

const ExperiencesContainer = dynamic(
  () =>
    import('@/components/experiences/ExperiencesContainer').then(
      m => m.ExperiencesContainer
    ),
  {
    ssr: false,
    loading: () => <ExperiencesLoadingWrapper />,
  }
);

export default function ExperiencesPage() {
  const router = useRouter();

  // Log page render
  React.useEffect(() => {
    console.log('📄 ExperiencesPage: Page component rendered');
  }, []);

  const handleNewChat = () => {
    router.push('/chat?new=1');
  };

  const handleLogoClick = () => {
    // Navigate to chat to show full landing page
    router.push('/chat?view=full-landing');
  };

  return (
    <ThemeProvider defaultMode='light'>
      <SeoHead
        title={DEFAULT_META_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        path='/experiences'
        disableTitleSuffix
      />
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: backgroundColors.primary,
          display: 'flex',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Sidebar */}
        <Sidebar onNewChat={handleNewChat} onLogoClick={handleLogoClick} />

        {/* Mobile Navbar */}
        <div className='md:hidden'>
          <MobileNavbar
            onNewChat={handleNewChat}
            onLogoClick={handleLogoClick}
          />
        </div>

        {/* Main Content */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            minHeight: 0,
          }}
          className='md:ml-[106px] w-full max-w-full overflow-x-hidden box-border flex flex-col'
        >
          <main
            style={{
              padding: spacing[4],
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              overflowX: 'hidden',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              minHeight: 0,
            }}
            className='p-4 w-full max-w-full overflow-x-hidden box-border flex flex-col items-center pt-[56px] md:pt-4'
          >
            <ExperiencesContainer />
          </main>

          {/* Footer - Full width outside main padding, stays at bottom */}
          <div
            style={{ flexShrink: 0 }}
            ref={el => {
              if (el) {
                console.log('🦶 Footer: Footer element rendered/mounted');
              }
            }}
          >
            <Footer />
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
